/*
 * The page-load re-sync (index.tsx, GEOV-122) refreshes the stored
 * `userData` from the UserById query. Merge instead of replacing it: a
 * field the query doesn't select must not be wiped. That's how `companyId`
 * went missing after the first reload, which broke project creation
 * ("No company is associated with your account.", GEOV-246).
 */
export function mergeRefreshedUserData(
  stored: Record<string, unknown>,
  fresh: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...stored };

  Object.entries(fresh).forEach(([key, value]) => {
    // A field the server didn't return keeps its stored value.
    if (value !== undefined) merged[key] = value;
  });

  return merged;
}

/*
 * The top bar and sidebar read their name from the stored `userData` once,
 * when they mount. After the user edits their own profile (GEOV-290) the
 * stored copy is updated here, and this event tells them to read it again,
 * so the new name shows without a reload.
 */
export const USER_DATA_UPDATED_EVENT = "userData:updated";

export function updateStoredUserData(fresh: Record<string, unknown>): void {
  let stored: Record<string, unknown> = {};
  try {
    stored = JSON.parse(localStorage.getItem("userData") || "{}") ?? {};
  } catch {
    // A corrupt copy is replaced by the fresh fields.
  }

  localStorage.setItem(
    "userData",
    JSON.stringify(mergeRefreshedUserData(stored, fresh)),
  );
  window.dispatchEvent(new Event(USER_DATA_UPDATED_EVENT));
}
